/**
 * Markup Indicator algebra (Story 17.4 — Epic 17 Ad Spend Markup, AC #10)
 *
 * Pure, React-free helper that decides whether the report editor's Data Source
 * section should show the agency-eyes-only "Includes N% markup" status line,
 * and what its copy is. Mirrors the Story 32.6 aggregation-indicator pattern:
 * all the decision logic lives here (a leaf lib module — no React, no fetch),
 * the component only renders the result.
 *
 * The "is this metric markup-affected for platform P" question is delegated to
 * the SHARED classifier in `@provenleap/integrations` (Story 17.4 AC #9) so the
 * editor and the server engine derive markup-affected keys from the SAME list.
 *
 * Markup resolution per platform (mirrors the server engine
 * `resolveMarkupForPlatform`): a platform-specific row wins over the client's
 * global default (`integrationType: null` from the GET /markups contract) wins
 * over no markup.
 */

import {
  getRegistryForPlatform,
  anyMetricMarkupAffected,
} from "@provenleap/integrations";
import type { ClientMarkupRow } from "../hooks/useClientMarkups";

/** The metric binding the indicator inspects (key + its platform slug). */
export interface IndicatorMetric {
  key: string;
  /** The metric's platform slug (DataSourceSection's SelectedMetric.integrationId). */
  integrationId: string;
}

/** The resolved markup config for one platform, or null when none applies. */
interface ResolvedMarkup {
  markupType: "percentage" | "fixed";
  markupValue: number;
}

/**
 * Resolve the markup config that applies to `platform`: platform-specific row
 * first, then the client's global default (`integrationType: null`). Null when
 * neither exists.
 */
function resolveMarkupForPlatform(
  markups: readonly ClientMarkupRow[],
  platform: string,
): ResolvedMarkup | null {
  const specific = markups.find((m) => m.integrationType === platform);
  const global = markups.find((m) => m.integrationType === null);
  const row = specific ?? global;
  return row ? { markupType: row.markupType, markupValue: row.markupValue } : null;
}

/** Whether any of `metricKeys` is markup-affected on `platform` (shared classifier). */
function platformHasAffectedMetric(
  platform: string,
  metricKeys: readonly string[],
): boolean {
  const registry = getRegistryForPlatform(
    platform as Parameters<typeof getRegistryForPlatform>[0],
  );
  if (!registry) return false;
  return anyMetricMarkupAffected(metricKeys, registry.metrics);
}

/**
 * Format the one-line indicator copy for a single resolved markup config.
 * `15` (percentage) → "Includes 15% markup"; `500` (fixed) → "Includes $500/mo markup".
 */
function formatMarkupCopy(markup: ResolvedMarkup): string {
  if (markup.markupType === "percentage") {
    // 15 reads "15%", 12.5 reads "12.5%" — String() drops no significant digits.
    return `Includes ${markup.markupValue}% markup`;
  }
  return `Includes $${markup.markupValue}/mo markup`;
}

/**
 * Compute the markup-indicator state for a widget's Data Source section.
 *
 * Returns `null` (indicator hidden) unless ALL gates pass (AC #10):
 *  (a) `clientId` present — passed by the caller as `hasClient`;
 *  (b) ≥1 selected metric is markup-affected on an involved platform;
 *  (c) that platform has an applicable markup configured.
 *
 * When multiple involved platforms each have an applicable markup and they
 * DIFFER (different type or value), the copy degrades to the generic
 * "Includes markup" (AC #10) — a single consistent config keeps its specific copy.
 */
export function computeMarkupIndicator(opts: {
  hasClient: boolean;
  involvedPlatforms: readonly string[];
  metrics: readonly IndicatorMetric[];
  markups: readonly ClientMarkupRow[];
}): { copy: string } | null {
  const { hasClient, involvedPlatforms, metrics, markups } = opts;
  if (!hasClient) return null;
  if (involvedPlatforms.length === 0 || metrics.length === 0) return null;
  if (markups.length === 0) return null;

  // Collect the applicable markup for every involved platform that BOTH has a
  // markup-affected selected metric AND a resolvable markup config.
  const applicable: ResolvedMarkup[] = [];
  for (const platform of involvedPlatforms) {
    // Metric keys bound to THIS platform (a multi-platform line chart pins each
    // metric to its own platform via integrationId).
    const keysOnPlatform = metrics
      .filter((m) => m.integrationId === platform)
      .map((m) => m.key);
    // Fallback: a single-platform widget may carry the metric under the widget
    // integrationId rather than per-metric — if no metric names this platform
    // explicitly but the widget is single-platform, consider all keys.
    const candidateKeys =
      keysOnPlatform.length > 0 ? keysOnPlatform : metrics.map((m) => m.key);
    if (!platformHasAffectedMetric(platform, candidateKeys)) continue;
    const markup = resolveMarkupForPlatform(markups, platform);
    if (markup) applicable.push(markup);
  }

  if (applicable.length === 0) return null;

  // All applicable configs identical → specific copy; otherwise generic.
  const first = applicable[0]!;
  const allSame = applicable.every(
    (m) => m.markupType === first.markupType && m.markupValue === first.markupValue,
  );
  return { copy: allSame ? formatMarkupCopy(first) : "Includes markup" };
}
